// SimpleIDESimulation.jsx

import React, { useEffect, useRef, useState } from "react";
import "./SimpleIDE.css"

import {
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    Clock,
    AmbientLight,
    PointLight,
    LoadingManager,
    Vector3,
    Object3D,
    BufferGeometry,
    Mesh,
    Line,
    LineBasicMaterial,
    AxesHelper,
    BoxGeometry,
    MeshBasicMaterial,
    DirectionalLight,
} from "three";

import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { ColladaLoader } from "three/examples/jsm/loaders/ColladaLoader";

import URDFLoader from "urdf-loader";
import axios from "axios";
import * as TWEEN from "@tweenjs/tween.js";

//import { MeshLineGeometry, MeshLineMaterial } from "meshline";


const SimpleIDESimulation = ({
    simid,
    simulationId,
}) => {

    const containerRef = useRef(null);
    const infoRef = useRef(null);

    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const clockRef = useRef(null);
    const controlsRef = useRef(null);

    const managerRef = useRef(null);
    const loaderRef = useRef(null);

    const astrobeeRef = useRef(null);
    const animationFrameRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isLoading, setIsLoading] = useState(true);


    // ---------------------------------------------------------
    // Paths to 3D models
    // ---------------------------------------------------------

    const issDaePath =
        "/static/astrobee/astrobee_iss/meshes/jpm.dae";

    const robotDaePath =
        "/static/astrobee/urdf/model.urdf";

    const dockDaePath =
        "/static/astrobee/astrobee_dock/urdf/model.urdf";


    // ---------------------------------------------------------
    // Load simulation data
    // ---------------------------------------------------------

    useEffect(() => {
        let cancelled = false;
        const id = 100 //simulationId || simid;

        if (!id) {
            setError("Simulation ID is missing.");
            setLoading(false);
            return;
        }


        const init = async () => {

            try {

                if (infoRef.current) {
                    infoRef.current.innerText =
                        "Loading in progress";
                }


                // -------------------------------------------------
                // Get simulation result
                // -------------------------------------------------

                const response = await axios.get(`/vis/astrobee/${id}`
                );

                if (cancelled) {
                    return;
                }

                const data = response.data.resource;

                if (!data) {
                    throw new Error(
                        "Simulation data is empty."
                    );
                }


                // -------------------------------------------------
                // Container
                // -------------------------------------------------

                const container = containerRef.current;

                if (!container) {
                    throw new Error(
                        "Simulation container not found."
                    );
                }


                // -------------------------------------------------
                // Renderer
                // -------------------------------------------------

                const canvas = document.createElement("canvas");

                const gl =
                    canvas.getContext("webgl2") ||
                    canvas.getContext("webgl") ||
                    canvas.getContext("experimental-webgl");

                console.log("WEBGL TEST:", gl);

                if (!gl) {
                    throw new Error(
                        "Browser cannot create a WebGL context."
                    );
                }

                const renderer =
                    new WebGLRenderer({
                        canvas,
                        antialias: true,
                    });

                renderer.setPixelRatio(
                    window.devicePixelRatio
                );

                renderer.setClearColor(0x000000);

                renderer.setSize(
                    container.clientWidth ||
                    window.innerWidth,

                    container.clientHeight ||
                    window.innerHeight
                );

                container.appendChild(
                    renderer.domElement
                );

                rendererRef.current = renderer;


                // -------------------------------------------------
                // Camera
                // -------------------------------------------------

                const cd = data.camera;
                const rd = data.robot;

                const camera =
                    new PerspectiveCamera(
                        cd.c.fov,

                        (
                            container.clientWidth ||
                            window.innerWidth
                        ) /
                        (
                            container.clientHeight ||
                            window.innerHeight
                        ),

                        cd.c.near,
                        cd.c.far
                    );

                camera.position.set(
                    cd.p.x,
                    cd.p.y,
                    cd.p.z
                );

                camera.lookAt(
                    new Vector3(
                        cd.r.x,
                        cd.r.y,
                        cd.r.z
                    )
                );

                cameraRef.current = camera;


                // -------------------------------------------------
                // Scene
                // -------------------------------------------------

                const scene = new Scene();

                scene.rotation.x =
                    -90 * Math.PI / 180;

                sceneRef.current = scene;


                // -------------------------------------------------
                // Clock
                // -------------------------------------------------

                clockRef.current = new Clock();


                // -------------------------------------------------
                // Lights
                // -------------------------------------------------

                const ambientLight =
                    new AmbientLight(
                        0xffffff,
                        1
                    );

                scene.add(ambientLight);


                const pointLight =
                    new PointLight(
                        0x000000,
                        2,
                        80
                    );

                scene.add(pointLight);


                const directionalLight =
                    new DirectionalLight(
                        0x000000,
                        1
                    );

                directionalLight.position.set(
                    -1,
                    2,
                    4
                );

                scene.add(
                    directionalLight
                );


                scene.add(camera);


                // -------------------------------------------------
                // Orbit controls
                // -------------------------------------------------

                const controls =
                    new OrbitControls(
                        camera,
                        renderer.domElement
                    );

                controls.screenSpacePanning = true;
                controls.enableDamping = true;
                controls.update();

                controlsRef.current = controls;


                // -------------------------------------------------
                // Axis
                // -------------------------------------------------

                createAxis(scene);


                // -------------------------------------------------
                // Loading manager
                // -------------------------------------------------

                const manager =
                    new LoadingManager();

                managerRef.current = manager;

                const loader =
                    new URDFLoader(manager);

                loaderRef.current = loader;


                // -------------------------------------------------
                // Loading status
                // -------------------------------------------------

                manager.onStart =
                    (
                        url,
                        itemsLoaded,
                        itemsTotal
                    ) => {

                        const message =
                            `Started loading file: ${url}. ` +
                            `Loaded ${itemsLoaded} of ${itemsTotal} files.`;

                        console.log(message);

                        if (infoRef.current) {
                            infoRef.current.innerText =
                                message;
                        }
                    };


                manager.onProgress = () => {
                    if (infoRef.current) {
                        infoRef.current.innerHTML = `
            <div class="loading-spinner"></div>
            <div>Loading simulation...</div>
        `;
                    }
                };


                manager.onError = (url) => {

                    const message =
                        `There was an error loading ${url}`;

                    console.error(message);

                    if (infoRef.current) {
                        infoRef.current.innerText =
                            message;
                    }
                };


                // -------------------------------------------------
                // Load ISS
                // -------------------------------------------------

                loadISS(
                    scene,
                    issDaePath
                );


                // -------------------------------------------------
                // Load dock
                // -------------------------------------------------

                loadDock(
                    scene,
                    loader,
                    dockDaePath
                );


                // -------------------------------------------------
                // Load Astrobee
                // -------------------------------------------------

                loadAstrobee(
                    scene,
                    loader,
                    robotDaePath,
                    rd[0],
                    astrobeeRef
                );


                // -------------------------------------------------
                // Load obstacles
                // -------------------------------------------------

                if (data.obs?.[0]) {
                    loadBox(
                        scene,
                        data.obs[0]
                    );
                }

                if (data.obs?.[1]) {
                    loadBox(
                        scene,
                        data.obs[1]
                    );
                }


                // -------------------------------------------------
                // Wait until URDF files finish loading
                // -------------------------------------------------

                manager.onLoad = () => {
                    setIsLoading(false);

                    if (cancelled) {
                        return;
                    }

                    const astrobee =
                        astrobeeRef.current;

                    if (!astrobee) {
                        console.error(
                            "Astrobee was not loaded."
                        );

                        return;
                    }


                    astrobee.traverse(
                        (object) => {

                            if (object instanceof Object3D) {
                                object.castShadow = true;
                            }
                        }
                    );


                    scene.add(astrobee);


                    startMovie(
                        data.tr,
                        astrobee,
                        rd[0],
                        scene
                    );


                    setLoading(false);

                    if (infoRef.current) {
                        infoRef.current.innerText =
                            "Simulation ready";
                    }


                    animate();
                };


                // -------------------------------------------------
                // Resize
                // -------------------------------------------------

                const handleResize = () => {

                    if (!cameraRef.current ||
                        !rendererRef.current ||
                        !containerRef.current) {
                        return;
                    }

                    const width =
                        containerRef.current.clientWidth ||
                        window.innerWidth;

                    const height =
                        containerRef.current.clientHeight ||
                        window.innerHeight;


                    cameraRef.current.aspect =
                        width / height;

                    cameraRef.current.updateProjectionMatrix();


                    rendererRef.current.setSize(
                        width,
                        height,
                        false
                    );
                };


                window.addEventListener(
                    "resize",
                    handleResize
                );


                // Initial resize
                handleResize();


                // Store cleanup function
                cleanupResize.current =
                    () => {

                        window.removeEventListener(
                            "resize",
                            handleResize
                        );
                    };

            }
            catch (err) {

                console.error(
                    "Simple IDE simulation error:",
                    err
                );

                if (!cancelled) {

                    setError(
                        err?.message ||
                        "Unable to load simulation."
                    );

                    setLoading(false);

                    if (infoRef.current) {
                        infoRef.current.innerText =
                            "Simulation failed";
                    }
                }
            }
        };


        init();


        return () => {

            cancelled = true;

            cleanupResize.current?.();

            if (animationFrameRef.current) {

                cancelAnimationFrame(
                    animationFrameRef.current
                );

                animationFrameRef.current =
                    null;
            }


            TWEEN.removeAll();


            if (controlsRef.current) {

                controlsRef.current.dispose();

                controlsRef.current = null;
            }


            if (sceneRef.current) {

                sceneRef.current.traverse(
                    (object) => {

                        if (object.geometry) {
                            object.geometry.dispose();
                        }


                        if (object.material) {

                            if (Array.isArray(
                                object.material
                            )) {

                                object.material.forEach(
                                    (material) =>
                                        material.dispose()
                                );

                            }
                            else {

                                object.material.dispose();
                            }
                        }
                    }
                );

                sceneRef.current.clear();

                sceneRef.current = null;
            }


            if (rendererRef.current) {

                rendererRef.current.dispose();

                const domElement =
                    rendererRef.current.domElement;

                if (
                    domElement &&
                    domElement.parentNode
                ) {

                    domElement.parentNode.removeChild(
                        domElement
                    );
                }

                rendererRef.current = null;
            }


            cameraRef.current = null;
            astrobeeRef.current = null;
            loaderRef.current = null;
            managerRef.current = null;
            clockRef.current = null;
        };

    }, [simid, simulationId]);


    // ---------------------------------------------------------
    // Resize cleanup ref
    // ---------------------------------------------------------

    const cleanupResize = useRef(null);


    // ---------------------------------------------------------
    // Start Astrobee movie
    // ---------------------------------------------------------

    const startMovie = (
        result,
        astrobee,
        robotData,
        scene
    ) => {

        if (!result || !astrobee) {
            return;
        }


        const tweens = [];

        let x =
            robotData.p.x - 9.685;

        let y =
            robotData.p.y + 5.6;

        let z =
            robotData.p.z - 5.6;


        const p1 =
            new Vector3(
                x,
                y,
                z
            );


        const positions = [];

        positions.push(p1);


        // -------------------------------------------------------
        // Create trajectory points
        // -------------------------------------------------------

        for (
            let i = 0;
            i < result.length;
            i++
        ) {

            const keyframe =
                result[i];

            const xx =
                keyframe.p.x - 9.685;

            const yy =
                keyframe.p.y + 5.6;

            const zz =
                keyframe.p.z - 5.6;


            const p =
                new Vector3(
                    xx,
                    yy,
                    -zz
                );

            positions.push(p);
        }


        let index = 0;


        // -------------------------------------------------------
        // Create animation tweens
        // -------------------------------------------------------

        for (
            let i = 0;
            i < result.length;
            i++
        ) {

            const keyframe =
                result[i];

            if (!keyframe) {
                continue;
            }


            const xx =
                keyframe.p.x - 9.685;

            const yy =
                keyframe.p.y + 5.6;

            const zz =
                keyframe.p.z - 5.6;


            const position =
                new Vector3(
                    xx,
                    yy,
                    -zz
                );


            const positionTween =
                new TWEEN.Tween(
                    astrobee.position
                )
                    .to(
                        position,
                        10000
                    )
                    .easing(
                        TWEEN.Easing.Quadratic.Out
                    );


            positionTween.onStart(() => {

                console.log(
                    "astrobee.position ",
                    astrobee.position
                );


                if (
                    positions[index] &&
                    positions[index + 1]
                ) {

                    createLine(
                        scene,
                        positions[index],
                        positions[index + 1]
                    );

                    index++;
                }
            });


            const rotation =
                new Vector3(
                    keyframe.r.x,
                    keyframe.r.y,
                    keyframe.r.z
                );


            const rotationTween =
                new TWEEN.Tween(
                    astrobee.rotation
                )
                    .to(
                        rotation,
                        1000
                    )
                    .easing(
                        TWEEN.Easing.Quadratic.Out
                    );


            tweens.push(
                positionTween
            );

            tweens.push(
                rotationTween
            );
        }


        // -------------------------------------------------------
        // Chain tweens
        // -------------------------------------------------------

        for (
            let i = 0;
            i < tweens.length - 1;
            i++
        ) {

            tweens[i].chain(
                tweens[i + 1]
            );
        }


        if (tweens.length > 0) {
            tweens[0].start();
        }
    };


    // ---------------------------------------------------------
    // Load ISS
    // ---------------------------------------------------------

    const loadISS = (
        scene,
        daePath
    ) => {

        const loader =
            new ColladaLoader();


        loader.load(
            daePath,
            (collada) => {

                const dae =
                    collada.scene;


                dae.position.y = 3;
                dae.position.x = 1.1;
                dae.position.z = 0.5;


                dae.rotation.z =
                    90 * Math.PI / 180;

                dae.rotation.x =
                    180 * Math.PI / 180;

                dae.rotation.y =
                    180 * Math.PI / 180;


                scene.add(dae);
            }
        );
    };


    // ---------------------------------------------------------
    // Load dock
    // ---------------------------------------------------------

    const loadDock = (
        scene,
        loader,
        daePath
    ) => {

        loader.load(
            daePath,
            (dock) => {

                dock.position.set(
                    1,
                    -2,
                    0
                );


                dock.rotation.z =
                    -180 * Math.PI / 180;


                scene.add(dock);
            }
        );
    };


    // ---------------------------------------------------------
    // Load Astrobee
    // ---------------------------------------------------------

    const loadAstrobee = (
        scene,
        loader,
        daePath,
        data,
        astrobeeRef
    ) => {

        console.log("Loading Astrobee:", daePath);

        loader.load(
            daePath,

            (result) => {

                console.log(
                    "ASTROBEE LOADED:",
                    result
                );

                astrobeeRef.current = result;

                console.log("ASTROBEE LOADED:", result);

                result.visible = true;

                result.traverse((child) => {
                    if (child.isMesh) {
                        console.log(
                            "ASTROBEE MESH:",
                            child.name,
                            "visible:",
                            child.visible,
                            "scale:",
                            child.scale
                        );

                        child.visible = true;
                        child.frustumCulled = false;
                    }
                });

                const x =
                    data.p.x - 9.685;

                const y =
                    data.p.y + 5.6;

                const z =
                    data.p.z - 5.6;

                result.position.set(
                    x,
                    y,
                    z
                );

                result.rotation.z =
                    data.r.z * Math.PI / 180;

                result.rotation.x =
                    data.r.x * Math.PI / 180;

                result.rotation.y =
                    data.r.y * Math.PI / 180;
            },

            (progress) => {
                console.log(
                    "Astrobee loading:",
                    progress
                );
            },

            (error) => {
                console.error(
                    "ASTROBEE LOAD ERROR:",
                    daePath,
                    error
                );
            }
        );
    };


    // ---------------------------------------------------------
    // Load obstacle box
    // ---------------------------------------------------------

    const loadBox = (
        scene,
        data
    ) => {

        const geometry =
            new BoxGeometry(
                data.s.w,
                data.s.h,
                data.s.d
            );


        const material =
            new MeshBasicMaterial({
                color: parseInt(
                    data.c.c
                ),

                opacity: data.c.o,

                transparent: data.c.t
            });


        const cube =
            new Mesh(
                geometry,
                material
            );


        cube.position.y =
            data.p.y;

        cube.position.x =
            data.p.x;

        cube.position.z =
            data.p.z;


        scene.add(cube);
    };


    // ---------------------------------------------------------
    // Create trajectory line
    // ---------------------------------------------------------

    function createLine(scene, start, end) {
        const geometry = new BufferGeometry();

        geometry.setFromPoints([
            new Vector3(start.x, start.y, start.z),
            new Vector3(end.x, end.y, end.z),
        ]);

        const material = new LineBasicMaterial({
            color: 0xff0000,
        });

        const line = new Line(geometry, material);

        scene.add(line);

        return line;
    }


    // ---------------------------------------------------------
    // Create axis
    // ---------------------------------------------------------

    const createAxis = (
        scene
    ) => {

        const axesHelper =
            new AxesHelper(10);

        scene.add(
            axesHelper
        );
    };


    // ---------------------------------------------------------
    // Animation loop
    // ---------------------------------------------------------

    const animate = () => {

        animationFrameRef.current =
            requestAnimationFrame(
                animate
            );


        TWEEN.update();


        render();
    };


    // ---------------------------------------------------------
    // Render
    // ---------------------------------------------------------

    const render = () => {

        const renderer =
            rendererRef.current;

        const scene =
            sceneRef.current;

        const camera =
            cameraRef.current;

        if (
            !renderer ||
            !scene ||
            !camera
        ) {
            return;
        }


        const clock =
            clockRef.current;


        if (clock) {
            clock.getDelta();
        }


        const canvas =
            renderer.domElement;


        const width =
            canvas.clientWidth;

        const height =
            canvas.clientHeight;


        if (
            canvas.width !== width ||
            canvas.height !== height
        ) {

            renderer.setSize(
                width,
                height,
                false
            );


            camera.aspect =
                width / height;


            camera.updateProjectionMatrix();
        }


        renderer.render(
            scene,
            camera
        );
    };


    // ---------------------------------------------------------
    // UI
    // ---------------------------------------------------------

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                background: "#000",
            }}
        >

            {isLoading && (
                <div className="simulation-loading">
                    <div className="simulation-loading-card">
                        <div className="simulation-spinner"></div>

                        <div className="simulation-loading-title">
                            Preparing Astrobee Simulation
                        </div>

                        <div className="simulation-loading-message">
                            Loading the spacecraft, space station environment,
                            and simulation resources...
                        </div>

                        <div className="simulation-loading-subtitle">
                            Please wait
                        </div>
                    </div>
                </div>
            )}

            <div
                ref={infoRef}
                style={{
                    position: "absolute",
                    top: "10px",
                    left: "10px",
                    zIndex: 10,
                    color: "#fff",
                    background:
                        "rgba(0,0,0,0.6)",
                    padding: "6px 10px",
                    fontSize: "13px",
                }}
            >
                {loading
                    ? "Loading in progress"
                    : error
                        ? error
                        : "Simulation ready"}
            </div>


            <div
                ref={containerRef}
                style={{
                    width: "100%",
                    height: "100%",
                }}
            />

        </div>
    );
};


export default SimpleIDESimulation;